import useSWR from 'swr';
import { getSlowQueries } from '../../api';
import type { SlowQueriesResponse, GetSlowQueriesParams } from './types/database';

interface UseSlowQueriesReturn {
  data: SlowQueriesResponse | undefined;
  isLoading: boolean;
  isError: any;
  mutate: () => void;
}

/**
 * Hook to fetch slow queries from pg_stat_statements
 *
 * @param params - Query parameters (hours, threshold_ms, limit)
 * @returns Slow queries data, loading state, error state, and mutate function
 *
 * @example
 * const { data, isLoading } = useSlowQueries({ hours: 24, threshold_ms: 1000, limit: 50 });
 */
export function useSlowQueries(params?: GetSlowQueriesParams): UseSlowQueriesReturn {
  const key = params
    ? `/admin/db-stats/slow-queries?${new URLSearchParams(Object.entries(params).map(([k, v]) => [k, String(v)])).toString()}`
    : '/admin/db-stats/slow-queries';

  const { data, error, mutate } = useSWR<SlowQueriesResponse>(
    key,
    () => getSlowQueries(params),
    {
      refreshInterval: 60000, // Refresh every minute
      revalidateOnMount: true,
    }
  );

  return {
    data,
    isLoading: !error && !data,
    isError: error,
    mutate,
  };
}
